import { useCallback, useState } from "react";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import Button, { ButtonProps } from "@scripts/react/components/Button";
import {
    AnnotationsForFileAndFrameId,
    usePromptLabels,
    useSetTextPromptMode,
    useTextPromptMode
} from "@/store";
import { AnalyticsFlowEvent } from "@/analytics/types";

import React from "react";
import { ModalWithIcon, ModalWithIconProps } from "@scripts/react/components/RfModal/ModalWithIcon";
import { useModel } from "@/contexts/ModelContext";

const ToggleTextPromptModeModal: React.FC<
    Omit<ModalWithIconProps, "title"> & { onToggle: () => void }
> = ({ onToggle, ...props }) => {
    return (
        <ModalWithIcon
            {...props}
            type="info"
            size="md"
            icon="fad fa-pencil"
            title="Want to edit these boxes?"
            onSubmit={onToggle}
            submitButtonProps={{ type: "secondary" }}
            submitButtonText="Switch to Editing Mode"
            onCancel={props.onClose}
            cancelButtonProps={{
                type: "purbo-ghost",
                className: "!bg-tint hover:!bg-tint-hover flex-1"
            }}
            cancelButtonText="Adjust Sliders"
            className="text-center"
        >
            <p>For the best model results, use the sliders instead of editing manually.</p>
            <p>
                Switching to <span className="font-semibold">Editing Mode</span> lets you draw and
                edit boxes, <br />
                but you'll need to restart to text prompt again.
            </p>
        </ModalWithIcon>
    );
};

export const ToggleTextPromptModeBtn = ({
    ...props
}: {} & Omit<ButtonProps, "onClick" | "children">) => {
    const { track } = useAnalytics();
    const textPromptMode = useTextPromptMode();
    const setTextPromptMode = useSetTextPromptMode();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const { setAnnotationsForFileAndFrameId } = useModel();
    const promptLabels = usePromptLabels();

    const onToggleTextPromptMode = useCallback(() => {
        const newMode = !textPromptMode;
        track({
            event: AnalyticsFlowEvent.TOGGLE_TEXT_PROMPT_MODE,
            data: { textPromptMode: newMode }
        });
        setAnnotationsForFileAndFrameId((prev) => {
            const newState: AnnotationsForFileAndFrameId = {};
            for (const fileId in prev) {
                for (const frameId in prev[fileId]) {
                    const annotations = prev[fileId][frameId];
                    const filteredAnnotations = annotations.filter((anno) => {
                        const promptLabel = promptLabels.get(anno.label);
                        if (!promptLabel) return false;
                        return (
                            !anno.confidence || anno.confidence >= promptLabel.confidenceThreshold
                        );
                    });
                    newState[fileId] = { ...newState[fileId], [frameId]: filteredAnnotations };
                }
            }
            return newState;
        });
        setTextPromptMode(newMode);
    }, [setTextPromptMode, textPromptMode, track, setAnnotationsForFileAndFrameId, promptLabels]);

    const onClickToggleTextPromptMode = useCallback(() => {
        track({
            event: AnalyticsFlowEvent.TOGGLE_TEXT_PROMPT_MODE_MODAL_OPENED,
            data: { textPromptMode }
        });
        setModalIsOpen(true);
    }, [textPromptMode, track]);

    if (!textPromptMode) {
        return null;
    }

    return (
        <div className="flex flex-col gap-4">
            <Button
                {...props}
                type="purbo-ghost"
                onClick={onClickToggleTextPromptMode}
                className="w-full"
            >
                {textPromptMode ? "Want to edit these boxes?" : "Switch to Text Prompting"}
            </Button>
            <ToggleTextPromptModeModal
                isOpen={modalIsOpen}
                onClose={() => setModalIsOpen(false)}
                onToggle={onToggleTextPromptMode}
            />
        </div>
    );
};
